/**
 * Workspace Settings → Members. Per-workspace member grants
 * (#526 PR 5a).
 *
 * Displays the rows from PR 1's `GET /api/workspaces/:wid/members`.
 * PR 5a covers direct USER grants only — team grants ship in PR 5b
 * alongside the Teams page.
 *
 * **Visibility gate (server-enforced).** The list endpoint itself is
 * admin-only on the server side — `GET /api/workspaces/:wid/members`
 * gates on `canManageWorkspaceMembers` (org Owner/Admin OR workspace
 * Admin). Non-authorized roles get a 403 on the GET and the page
 * surfaces the load error. The parent `WorkspaceSettings` index hides
 * the Members tab entirely for roles that can't manage, so this
 * component only ever renders for admins; `canManage` below is
 * effectively always true and only gates the action affordances as
 * belt-and-suspenders. PR #540 Copilot review.
 *
 * Display enrichment (#2094): membership rows carry ids only, so the
 * list endpoint attaches `users` and `teams` maps naming the subject
 * and the grantor. Both are rendered through `lib/userDisplay`'s
 * name → email → id fallback, which is shared with the team member
 * list. An id that fails to resolve still renders as an id; the full
 * value stays available in a tooltip for support.
 *
 * Granting access (#2095): the user side of the grant form searches the
 * organization's members by name or email instead of asking for a raw
 * database id. Someone who already holds a direct grant here shows in
 * the list marked and unpickable, so searching for them answers the
 * question rather than returning nothing.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button, Label, Card, CardContent } from '@/components/ui'
import type { Team, UserDisplayMap, WorkspaceMembership, WorkspaceSummary } from '@/lib/api'
import { teamsAPI, workspaceMembersAPI } from '@/lib/api'
import { isUserResolved, userDisplayName, userDisplaySecondary } from '@/lib/userDisplay'
import { PersonPicker } from '@/components/PersonPicker'
import { useCanIWorkspace } from '@/hooks/useCanI'
import { useCanI } from '@/hooks/useCanI'
import { useConfirm } from '@/hooks/useConfirm'

type WorkspaceRole = 'admin' | 'tester' | 'reviewer'

const ROLE_OPTIONS: WorkspaceRole[] = ['admin', 'tester', 'reviewer']

type TeamNameMap = Record<string, { name: string }>

/** What to call the thing a grant was made to. A row's `subjectId` is
 * a userId or a teamId depending on `subjectType`, so the two cases
 * read from different maps — this is the reason the endpoint returns
 * separate sidecars rather than one flattened name per row. Falls
 * back to the raw id when the subject can't be resolved. */
function subjectLabel(
  row: WorkspaceMembership,
  users: UserDisplayMap,
  teams: TeamNameMap,
): string {
  if (row.subjectType === 'team') return teams[row.subjectId]?.name ?? row.subjectId
  return userDisplayName(users, row.subjectId)
}

export function WorkspaceMembers({ workspace }: { workspace: WorkspaceSummary }) {
  // The manage gate at the workspace tier (workspace Admin grants
  // membership management). The org-tier gate (org Owner / Admin
  // reach on ANY workspace) is folded in by useCanI on the org id —
  // either path admits the user. Same shape as the server's
  // canManageWorkspaceMembers inline check.
  const canManageViaWorkspace = useCanIWorkspace('workspaceMembers.grant', workspace._id)
  const canManageViaOrg = useCanI('members.invite', workspace.orgId)
  const canManage = canManageViaWorkspace || canManageViaOrg
  // #1148: shared themed confirm, replacing native confirm().
  const confirm = useConfirm()

  const [rows, setRows] = useState<WorkspaceMembership[]>([])
  const [users, setUsers] = useState<UserDisplayMap>({})
  const [teams, setTeams] = useState<TeamNameMap>({})
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setLoadError(null)
      const list = await workspaceMembersAPI.list(workspace._id)
      setRows(list.memberships)
      // Default to empty maps rather than trusting the field to be
      // present: the server's lookup is fail-soft and every consumer
      // already treats a missing entry as "render the id".
      setUsers(list.users ?? {})
      setTeams(list.teams ?? {})
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err))
    }
  }, [workspace._id])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleRoleChange(row: WorkspaceMembership, newRole: WorkspaceRole) {
    if (row.role === newRole) return
    setActionError(null)
    setBusy(true)
    try {
      await workspaceMembersAPI.updateRole(workspace._id, row.subjectType, row.subjectId, newRole)
      await refresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleRevoke(row: WorkspaceMembership) {
    const ok = await confirm({
      title: `Remove ${subjectLabel(row, users, teams)}'s access to this workspace?`,
      description:
        row.subjectType === 'team'
          ? 'Everyone in this team loses access to this workspace immediately.'
          : 'They lose access to this workspace immediately.',
      confirmLabel: 'Revoke',
      destructive: true,
    })
    if (!ok) return
    setActionError(null)
    setBusy(true)
    try {
      await workspaceMembersAPI.revoke(workspace._id, row.subjectType, row.subjectId)
      await refresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">Failed to load members: {loadError}</p>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>Retry</Button>
      </div>
    )
  }

  const userRows = rows.filter((r) => r.subjectType === 'user')
  const teamRows = rows.filter((r) => r.subjectType === 'team')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Workspace members</h3>
          <p className="text-sm text-muted-foreground">
            Users and teams granted access to <strong>{workspace.name}</strong>.
          </p>
        </div>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => setShowAdd((s) => !s)}>
            {showAdd ? 'Cancel' : '+ Grant access'}
          </Button>
        )}
      </div>

      {showAdd && canManage && (
        <GrantAccessForm
          workspaceId={workspace._id}
          orgId={workspace.orgId}
          // Direct user grants already on this workspace. Someone
          // holding one can't be granted again (the unique index
          // refuses it); the picker marks them rather than hiding
          // them, so searching for them gives an answer.
          existingUserIds={rows
            .filter((r) => r.subjectType === 'user')
            .map((r) => r.subjectId)}
          onCancel={() => setShowAdd(false)}
          onGranted={async () => {
            setShowAdd(false)
            await refresh()
          }}
        />
      )}

      {actionError && (
        <p className="text-sm text-destructive" role="alert">{actionError}</p>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Subject</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Granted by</th>
                {canManage && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {userRows.length === 0 && teamRows.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-muted-foreground" colSpan={canManage ? 4 : 3}>
                    No members yet.
                  </td>
                </tr>
              )}
              {userRows.map((row) => (
                <MembershipRow
                  key={row._id}
                  row={row}
                  users={users}
                  teams={teams}
                  canManage={canManage}
                  busy={busy}
                  onRoleChange={(r) => void handleRoleChange(row, r)}
                  onRevoke={() => void handleRevoke(row)}
                />
              ))}
              {teamRows.map((row) => (
                <MembershipRow
                  key={row._id}
                  row={row}
                  users={users}
                  teams={teams}
                  canManage={canManage}
                  busy={busy}
                  onRoleChange={(r) => void handleRoleChange(row, r)}
                  onRevoke={() => void handleRevoke(row)}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

    </div>
  )
}

function MembershipRow({
  row,
  users,
  teams,
  canManage,
  busy,
  onRoleChange,
  onRevoke,
}: {
  row: WorkspaceMembership
  users: UserDisplayMap
  teams: TeamNameMap
  canManage: boolean
  busy: boolean
  onRoleChange: (role: WorkspaceRole) => void
  onRevoke: () => void
}) {
  const subject = subjectLabel(row, users, teams)
  // Only user subjects have a second line to show; a team has a name
  // and nothing beneath it.
  const subjectEmail =
    row.subjectType === 'user' ? userDisplaySecondary(users, row.subjectId) : null
  // When the label IS the id (unresolved), the id is already on
  // screen and a tooltip repeating it is noise.
  const subjectResolved =
    row.subjectType === 'team'
      ? Boolean(teams[row.subjectId])
      : isUserResolved(users, row.subjectId)
  const grantor = userDisplayName(users, row.grantedBy)
  const grantorResolved = isUserResolved(users, row.grantedBy)

  return (
    <tr className="border-b last:border-b-0">
      <td className="px-4 py-2">
        <span className="rounded bg-muted px-1.5 py-0.5 mr-2 text-xs uppercase font-mono">
          {row.subjectType}
        </span>
        <span
          className={subjectResolved ? 'text-sm' : 'font-mono text-xs'}
          title={subjectResolved ? row.subjectId : undefined}
        >
          {subject}
        </span>
        {subjectEmail && (
          <div className="ml-[3.25rem] text-xs text-muted-foreground">{subjectEmail}</div>
        )}
      </td>
      <td className="px-4 py-2">
        {canManage ? (
          <select
            className="border rounded px-2 py-1 text-sm"
            disabled={busy}
            value={row.role}
            onChange={(e) => onRoleChange(e.target.value as WorkspaceRole)}
            aria-label={`Role for ${subject}`}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        ) : (
          <span className="text-sm">{row.role}</span>
        )}
      </td>
      <td className="px-4 py-2 text-muted-foreground">
        <span
          className={grantorResolved ? 'text-sm' : 'font-mono text-xs'}
          title={grantorResolved ? row.grantedBy : undefined}
        >
          {grantor}
        </span>
        {row.grantedOrigin === 'workspace-creator-auto' && (
          <span className="ml-2 text-xs italic">(creator)</span>
        )}
      </td>
      {canManage && (
        <td className="px-4 py-2 text-right">
          <Button variant="destructive" size="sm" disabled={busy} onClick={onRevoke}>
            Revoke
          </Button>
        </td>
      )}
    </tr>
  )
}

function GrantAccessForm({
  workspaceId,
  orgId,
  existingUserIds,
  onCancel,
  onGranted,
}: {
  workspaceId: string
  orgId: string
  existingUserIds: readonly string[]
  onCancel: () => void
  onGranted: () => Promise<void>
}) {
  // Subject toggle: user or team. Both sides are now pickers — the
  // user side searched the org's members by name or email (#2095),
  // replacing a text box that required a raw database id.
  const [subjectType, setSubjectType] = useState<'user' | 'team'>('user')
  const [userId, setUserId] = useState('')
  const [teamId, setTeamId] = useState('')
  const [role, setRole] = useState<WorkspaceRole>('tester')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Lazy-load the org's teams when the user flips to the team subject.
  const [teams, setTeams] = useState<Team[] | null>(null)
  const [teamsLoading, setTeamsLoading] = useState(false)
  async function ensureTeamsLoaded() {
    if (teams !== null || teamsLoading) return
    setTeamsLoading(true)
    try {
      const list = await teamsAPI.list(orgId)
      setTeams(list)
      if (list.length > 0 && !teamId) setTeamId(list[0]._id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setTeamsLoading(false)
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const subjectId = subjectType === 'user' ? userId.trim() : teamId
    if (!subjectId) {
      setError(subjectType === 'user' ? 'Pick a person first.' : 'Pick a team first.')
      return
    }
    setBusy(true)
    try {
      await workspaceMembersAPI.grant(workspaceId, { subjectType, subjectId, role })
      await onGranted()
      setUserId('')
      setTeamId('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded border p-4 space-y-3">
      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            name="subject-type"
            checked={subjectType === 'user'}
            onChange={() => setSubjectType('user')}
          />
          User
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            name="subject-type"
            checked={subjectType === 'team'}
            onChange={() => {
              setSubjectType('team')
              void ensureTeamsLoaded()
            }}
          />
          Team
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3">
        <div>
          {subjectType === 'user' ? (
            <>
              <Label htmlFor="grant-userid">Person</Label>
              <PersonPicker
                id="grant-userid"
                orgId={orgId}
                value={userId}
                onChange={setUserId}
                alreadyHasIds={existingUserIds}
                alreadyHasLabel="Already has access"
              />
            </>
          ) : (
            <>
              <Label htmlFor="grant-teamid">Team</Label>
              <select
                id="grant-teamid"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                disabled={teamsLoading || !teams}
              >
                {teamsLoading && <option value="">Loading teams…</option>}
                {!teamsLoading && (!teams || teams.length === 0) && (
                  <option value="">No teams in this org yet</option>
                )}
                {teams?.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        <div>
          <Label htmlFor="grant-role">Role</Label>
          <select
            id="grant-role"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs"
            value={role}
            onChange={(e) => setRole(e.target.value as WorkspaceRole)}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <Button type="submit" disabled={busy}>Grant</Button>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>
        </div>
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      <p className="text-xs text-muted-foreground">
        {subjectType === 'user'
          ? 'User must already be a member of this workspace\'s org.'
          : 'Granting a team gives every team member the chosen role on this workspace; individual members can be added via Org Settings → Teams.'}
      </p>
    </form>
  )
}
